import { useEffect, useState } from 'react';
import { X, Cloud, CloudOff, LogIn, LogOut } from 'lucide-react';
import { signIn, signOut } from '../../lib/auth';
import { useSession } from '../../lib/useAuth';
import { isCloudConfigured } from '../../lib/pocketbaseClient';
import { showToast } from '../../lib/toast';

interface AuthSheetProps {
  onClose: () => void;
}

type Stage =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string };

export function AuthSheet({ onClose }: AuthSheetProps): JSX.Element {
  const session = useSession();
  const [email, setEmail] = useState('thomas@voelker.digital');
  const [password, setPassword] = useState('');
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Wenn Login erfolgreich war, Sheet schliessen
  useEffect(() => {
    if (session && stage.kind === 'submitting') {
      onClose();
    }
  }, [session, stage.kind, onClose]);

  const cloudConfigured = isCloudConfigured();

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setStage({ kind: 'submitting' });
    const result = await signIn(email, password);
    if (result.ok) {
      showToast('Eingeloggt — Cloud-Sync aktiv', { variant: 'success' });
      // Sheet schliesst sich via Session-Effect
    } else {
      setStage({ kind: 'error', message: result.error ?? 'Login fehlgeschlagen.' });
    }
  };

  const handleSignOut = (): void => {
    signOut();
    showToast('Abgemeldet', { variant: 'info' });
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-sheet-title"
      className="fixed inset-0 z-50 flex flex-col bg-zinc-50"
    >
      <header
        className="sticky top-0 z-10 border-b border-zinc-200 bg-zinc-50/95 backdrop-blur"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex h-14 items-center gap-2 px-4">
          <h1
            id="auth-sheet-title"
            className="min-w-0 flex-1 truncate text-base font-semibold text-zinc-900"
          >
            Cloud-Sync
          </h1>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schliessen"
            className="-mr-2 inline-flex size-11 items-center justify-center rounded-lg text-zinc-900 active:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2"
          >
            <X aria-hidden="true" className="size-5" />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-md px-4 py-6 md:px-6 md:py-8">
          {!cloudConfigured ? <NotConfiguredCard /> : null}
          {cloudConfigured && session ? (
            <SignedInCard email={session.email} onSignOut={handleSignOut} />
          ) : null}
          {cloudConfigured && !session ? (
            <SignInCard
              email={email}
              password={password}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onSubmit={handleSubmit}
              busy={stage.kind === 'submitting'}
              error={stage.kind === 'error' ? stage.message : null}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function NotConfiguredCard(): JSX.Element {
  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="mb-2 flex items-center gap-2">
        <CloudOff aria-hidden="true" className="size-5 text-amber-700" />
        <h2 className="text-base font-semibold text-amber-900">
          Cloud-Sync nicht konfiguriert
        </h2>
      </div>
      <p className="text-sm leading-normal text-amber-900">
        Aktuell laeuft die App nur lokal. Trainingsfortschritt bleibt im Browser dieses
        Geraets gespeichert. Setup-Anleitung in{' '}
        <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">
          docs/pocketbase-setup.md
        </code>
        .
      </p>
    </section>
  );
}

interface SignedInCardProps {
  email: string;
  onSignOut: () => void;
}

function SignedInCard({ email, onSignOut }: SignedInCardProps): JSX.Element {
  return (
    <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
      <div className="mb-2 flex items-center gap-2">
        <Cloud aria-hidden="true" className="size-5 text-emerald-700" />
        <h2 className="text-base font-semibold text-emerald-900">Cloud-Sync aktiv</h2>
      </div>
      <p className="text-sm leading-normal text-emerald-900">
        Eingeloggt als <strong>{email}</strong>. Aenderungen werden automatisch zwischen
        deinen Geraeten synchronisiert.
      </p>
      <button
        type="button"
        onClick={onSignOut}
        className="mt-4 inline-flex h-11 items-center gap-2 rounded-lg border border-emerald-300 bg-white px-3 text-sm font-semibold text-emerald-900 active:bg-emerald-100 focus-visible:outline-2 focus-visible:outline-emerald-700 focus-visible:outline-offset-2"
      >
        <LogOut aria-hidden="true" className="size-4" />
        Abmelden
      </button>
    </section>
  );
}

interface SignInCardProps {
  email: string;
  password: string;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void | Promise<void>;
  busy: boolean;
  error: string | null;
}

function SignInCard({
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  busy,
  error,
}: SignInCardProps): JSX.Element {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="mb-2 flex items-center gap-2">
        <Cloud aria-hidden="true" className="size-5 text-zinc-700" />
        <h2 className="text-base font-semibold text-zinc-900">Anmelden</h2>
      </div>
      <p className="mb-4 text-sm leading-normal text-zinc-700">
        Damit dein Trainingsfortschritt zwischen Handy und Laptop synchronisiert.
      </p>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-600">
            E-Mail
          </span>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            className="block h-12 w-full rounded-lg border border-zinc-300 bg-white px-3 text-base text-zinc-900 focus-visible:border-emerald-500 focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-zinc-600">
            Passwort
          </span>
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            className="block h-12 w-full rounded-lg border border-zinc-300 bg-white px-3 text-base text-zinc-900 focus-visible:border-emerald-500 focus-visible:outline-2 focus-visible:outline-emerald-500 focus-visible:outline-offset-2"
          />
        </label>
        {error ? (
          <p role="alert" className="text-sm text-rose-700">
            {error}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={busy}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 text-base font-semibold text-white shadow-sm transition-transform duration-[120ms] active:scale-[0.98] active:bg-emerald-600 focus-visible:outline-2 focus-visible:outline-emerald-700 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <LogIn aria-hidden="true" className="size-5" />
          {busy ? 'Pruefe…' : 'Anmelden'}
        </button>
      </form>
    </section>
  );
}
